import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";

interface SearchesListSkeletonProps {
  items?: number;
  className?: string;
}

const metricSkeletons = Array.from({ length: 4 });
const statSkeletons = Array.from({ length: 4 });
const badgeSkeletons = ["w-20", "w-24", "w-16"] as const;

export function SearchesListSkeleton({
  items = 3,
  className,
}: SearchesListSkeletonProps) {
  return (
    <div className={cn("space-y-4", className)} data-testid="searches-list-skeleton">
      {Array.from({ length: items }).map((_, index) => (
        <Card
          key={index}
          className="overflow-hidden rounded-xl border border-brand-ink/12 bg-card shadow-sm"
          data-testid="searches-list-skeleton-card"
        >
          <CardContent className="pointer-events-none p-0">
            <div className="flex items-start justify-between gap-4 px-4 pt-3">
              <div className="min-w-0 flex-1 space-y-2">
                <Skeleton className="h-5 w-44 max-w-[65%]" />
                <Skeleton className="h-3.5 w-80 max-w-[90%]" />
              </div>
              <div className="flex shrink-0 items-center gap-2">
                <Skeleton className="h-9 w-9 rounded-lg" />
                <Skeleton className="h-9 w-9 rounded-lg" />
              </div>
            </div>

            <div className="grid grid-cols-1 gap-4 px-4 py-3 md:grid-cols-12">
              <div className="space-y-2 md:col-span-4">
                <Skeleton className="h-8 w-full max-w-52 rounded-full" />
                <Skeleton className="h-8 w-full max-w-48 rounded-full" />
                <div className="flex flex-wrap items-center gap-3 pt-1">
                  {statSkeletons.map((_, statIndex) => (
                    <Skeleton key={statIndex} className="h-4 w-10 rounded" />
                  ))}
                </div>
              </div>

              <div className="flex flex-col items-start gap-2 md:col-span-5 md:items-center">
                <Skeleton className="h-3 w-28" />
                <div className="flex w-full flex-wrap items-end justify-start gap-3 md:justify-center">
                  {metricSkeletons.map((_, metricIndex) => (
                    <div key={metricIndex} className="space-y-2">
                      <Skeleton className="h-[72px] w-[72px] rounded-xl" />
                      <Skeleton className="mx-auto h-3 w-12" />
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex items-start md:col-span-3 md:items-end md:justify-end">
                <div className="space-y-1 md:text-right">
                  <Skeleton className="h-3 w-16 md:ml-auto" />
                  <Skeleton className="h-3 w-24 md:ml-auto" />
                </div>
              </div>
            </div>

            <div className="flex flex-col gap-3 border-t border-brand-ink/[0.08] bg-brand-ink/[0.02] px-4 py-2 md:flex-row md:items-center md:justify-between">
              <div className="flex flex-wrap items-center gap-2">
                {badgeSkeletons.map((widthClassName) => (
                  <Skeleton key={widthClassName} className={`h-6 rounded-full ${widthClassName}`} />
                ))}
              </div>
              <div className="flex items-center gap-2">
                <Skeleton className="h-6 w-6 rounded-full" />
                <Skeleton className="h-3 w-20" />
              </div>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
